/**
 * AGC: Hook to check for refundable PvE battles
 * AGC: Scans when player connects, similar to airdrop claim check
 */

import { useEffect, useState, useRef, useCallback } from 'react'
import { ethers } from 'ethers'
import { buildBackendUrl } from '../utils/backendUrl'

interface RefundableBattle {
  battleId: string
  amount: number // AGC: Refund amount in OGB
  nfts: number[] // AGC: Escrowed NFT IDs
  canRefundAt: number // AGC: Unix timestamp (ms) when refund becomes available
  type: 'standard' | 'emergency' // AGC: Refund scenario (opponent failed vs both paid)
  category: 'pve' | 'pvp' // AGC: Contract type (PvE manager vs P2P manager)
  mode?: 'onchain' | 'hybrid' // AGC: PvE mode (for display)
  botLevel?: number | null // AGC: PvE bot level (for display)
  opponentAddress?: string | null // AGC: P2P opponent address
  opponentPaid?: boolean // AGC: Indicates if opponent already paid (emergency refunds)
}

// AGC: Global cache to prevent duplicate API calls across multiple hook instances
const refundCache = new Map<string, { data: RefundableBattle[], timestamp: number }>()
const CACHE_DURATION = 60000 // AGC: Increased to 60 seconds to reduce backend calls
const pendingChecks = new Set<string>() // AGC: Track addresses with checks in progress

/**
 * AGC: Hook to check for refundable PvE battles
 * AGC: Similar to airdrop claim system - scans when player connects
 * @param playerAddress - Player wallet address
 * @param autoCheck - If true, automatically checks on mount. Default: false (only check when MainMenu loads)
 */
export function usePvERefunds(playerAddress?: string, autoCheck: boolean = false) {
  const [refundableBattles, setRefundableBattles] = useState<RefundableBattle[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const lastLoggedErrorRef = useRef<string | null>(null)

  // AGC: Memoize checkRefundableBattles to prevent unnecessary re-renders
  const checkRefundableBattles = useCallback(async () => {
    if (!playerAddress) return

    const addressKey = playerAddress.toLowerCase()

    // AGC: Prevent duplicate simultaneous checks for the same address
    if (pendingChecks.has(addressKey)) {
      console.log(`⏸️  Refund check already in progress for ${playerAddress.slice(0, 10)}`)
      return
    }

    // AGC: Check cache first (before making API calls)
    const cached = refundCache.get(addressKey)
    const now = Date.now()
    
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      console.log(`📦 Using cached refund data for ${playerAddress.slice(0, 10)}`)
      setRefundableBattles(cached.data)
      return
    }

    // AGC: Mark check as in progress
    pendingChecks.add(addressKey)

    // AGC: Abort previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()
    setIsLoading(true)
    setError(null)

    try {
      // AGC: Call backend to check refundable battles
      const fetchEndpoint = async (path: string, label: string) => {
        try {
          const response = await fetch(
            buildBackendUrl(path),
            { signal: abortControllerRef.current?.signal }
          )

          if (!response.ok) {
            throw new Error(`Backend ${label} refund endpoint returned status ${response.status}`)
          }

          return await response.json()
        } catch (err: any) {
          if (err?.name === 'AbortError') {
            throw err
          }

          const message = err?.message || `Unable to check ${label} refunds`
          if (lastLoggedErrorRef.current !== message) {
            console.warn(`⚠️  ${label} refund check warning:`, message)
            lastLoggedErrorRef.current = message
          }

          return { refundableBattles: [] }
        }
      }

      const [pveData, pvpData] = await Promise.all([
        fetchEndpoint(`api/pve/refundable/${playerAddress}`, 'PvE'),
        fetchEndpoint(`api/p2p/refundable/${playerAddress}`, 'P2P')
      ])

      const pveBattles = (pveData?.refundableBattles || []).map((battle: any) => ({
        ...battle,
        category: battle.category ?? 'pve',
        mode: battle.mode ?? 'hybrid',
        botLevel: battle.botLevel ?? null,
        opponentAddress: null
      }))

      const pvpBattles = (pvpData?.refundableBattles || []).map((battle: any) => ({
        ...battle,
        category: 'pvp',
        mode: 'pvp',
        botLevel: null,
        opponentAddress: battle.opponentAddress || null
      }))

      const combinedBattles: RefundableBattle[] = [...pveBattles, ...pvpBattles]

      setRefundableBattles(combinedBattles)

      refundCache.set(playerAddress.toLowerCase(), {
        data: combinedBattles,
        timestamp: Date.now()
      })

      console.log(
        `🔍 Found ${combinedBattles.length} refundable battles ` +
        `(PvE: ${pveBattles.length}, P2P: ${pvpBattles.length}) for ${playerAddress.slice(0, 10)}`
      )

      lastLoggedErrorRef.current = null
      setError(null)

    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('🔄 Refund check aborted (duplicate request)')
        return
      }
      const message = err?.message || 'Unable to check refundable battles'
      if (lastLoggedErrorRef.current !== message) {
        console.warn('⚠️  Refund check warning:', message)
        lastLoggedErrorRef.current = message
      }
      setRefundableBattles([])
      setError(message)
    } finally {
      setIsLoading(false)
      // AGC: Remove from pending checks when done
      pendingChecks.delete(addressKey)
    }
  }, [playerAddress])

  useEffect(() => {
    if (!playerAddress) {
      setRefundableBattles([])
      return
    }

    // AGC: Only auto-check if autoCheck is enabled (default: false)
    // AGC: This prevents continuous checking - refunds are checked only when MainMenu loads
    if (!autoCheck) {
      // AGC: Still load from cache if available, but don't make API call
      const cached = refundCache.get(playerAddress.toLowerCase())
      const now = Date.now()
      
      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        console.log(`📦 Using cached refund data for ${playerAddress.slice(0, 10)}`)
        setRefundableBattles(cached.data)
      }
      return
    }

    // AGC: Auto-check enabled - check cache first, then API if needed
    const cached = refundCache.get(playerAddress.toLowerCase())
    const now = Date.now()
    
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      console.log(`📦 Using cached refund data for ${playerAddress.slice(0, 10)}`)
      setRefundableBattles(cached.data)
      return
    }

    checkRefundableBattles()

    // AGC: Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [playerAddress, checkRefundableBattles, autoCheck])

  const claimRefund = async (battleId: string) => {
    if (!window.ethereum) {
      throw new Error('No wallet detected')
    }

    try {
      const provider = new ethers.BrowserProvider(window.ethereum)
      const signer = await provider.getSigner()

      // AGC: Get V4 or V5 contract address based on battle type
      // AGC: For now, we'll use V5 as it's the most recent
      const battle = refundableBattles.find(b => b.battleId === battleId)
      
      if (!battle) {
        throw new Error('Battle not found')
      }

      // AGC: Verify battle is still refundable on-chain before attempting refund
      if (battle.category === 'pvp') {
        const P2P_MANAGER_ADDRESS = import.meta.env.VITE_P2P_MANAGER_ADDRESS
        if (P2P_MANAGER_ADDRESS) {
          try {
            // AGC: Detect contract version
            let isV2Contract = false
            try {
              const checkAbi = ['function trustedBuff1155Contracts(address) public view returns (bool)']
              const checkContract = new ethers.Contract(P2P_MANAGER_ADDRESS, checkAbi, provider)
              await checkContract.trustedBuff1155Contracts('0x0000000000000000000000000000000000000000')
              isV2Contract = true
            } catch {
              isV2Contract = false
            }
            
            const checkAbi = isV2Contract
              ? ['function battles(bytes32) public view returns (bytes32 battleId, address player1, address player2, uint256 stake, address ogbTokenAddress, bool player1Paid, bool player2Paid, bool resolved, address winner, uint256 createdAt, uint256 expiresAt, uint256[] player1BuffIds, uint256[] player1PotionIds, uint256[] player2BuffIds, uint256[] player2PotionIds)']
              : ['function battles(bytes32) public view returns (bytes32 battleId, address player1, address player2, uint256 stake, bool player1Paid, bool player2Paid, bool resolved, address winner, uint256 createdAt, uint256 expiresAt)']
            
            const checkContract = new ethers.Contract(P2P_MANAGER_ADDRESS, checkAbi, provider)
            const onChainBattle = await checkContract.battles(battleId)
            const isResolved = isV2Contract
              ? Boolean(onChainBattle.resolved ?? onChainBattle[7])
              : Boolean(onChainBattle.resolved ?? onChainBattle[6])
            
            if (isResolved) {
              // AGC: Battle already resolved/refunded - refresh list and throw error
              await checkRefundableBattles()
              throw new Error('This battle has already been resolved or refunded')
            }
          } catch (error: any) {
            // AGC: If it's a "battle not found" or "already resolved" error, refresh and throw
            if (error.message?.includes('already resolved') || error.message?.includes('already been resolved')) {
              await checkRefundableBattles()
              throw error
            }
            // AGC: Other errors (like decode errors) - continue with refund attempt
            console.warn('⚠️  Could not verify battle state on-chain, proceeding with refund:', error.message)
          }
        }
      }

      console.log(`💰 Claiming refund for battle ${battleId.slice(0, 10)}...`)
      console.log(`   Type: ${battle.type} • Category: ${battle.category}`)
      console.log(`   Amount: ${battle.amount} OGB + ${battle.nfts.length} NFT(s)`)

      let tx

      if (battle.category === 'pvp') {
        const P2P_MANAGER_ADDRESS = import.meta.env.VITE_P2P_MANAGER_ADDRESS
        const BUFF1155_ADDRESS = import.meta.env.VITE_BUFF1155_ADDRESS
        const OGB_TOKEN_ADDRESS = import.meta.env.VITE_OGB_TOKEN_ADDRESS
        const POTION1155_ADDRESS = import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS

        if (!P2P_MANAGER_ADDRESS) {
          throw new Error('P2P Manager address not configured')
        }

        if (!BUFF1155_ADDRESS) {
          throw new Error('Buff1155 address not configured')
        }
        
        if (!OGB_TOKEN_ADDRESS) {
          throw new Error('OGB Token address not configured')
        }
        
        // AGC: Detect contract version (V1 or V2)
        let isV2Contract = false
        try {
          // AGC: Try to call V2 function - if it exists, we have V2
          const checkAbi = ['function trustedBuff1155Contracts(address) public view returns (bool)']
          const checkContract = new ethers.Contract(P2P_MANAGER_ADDRESS, checkAbi, provider)
          await checkContract.trustedBuff1155Contracts(BUFF1155_ADDRESS)
          isV2Contract = true
          console.log('[OK] Detected P2PBattleManagerV2 for refund')
        } catch (error) {
          // AGC: V1 contract doesn't have trustedBuff1155Contracts
          isV2Contract = false
          console.log('[OK] Detected legacy P2PBattleManager (V1) for refund')
        }

        const P2P_MANAGER_ABI = isV2Contract
          ? [
              // AGC: V2 signatures
              'function expireBattle(bytes32 battleId, address ogbTokenAddress, address buff1155Address, address potion1155Address) external',
              'function emergencyCancelBattle(bytes32 battleId, address ogbTokenAddress, address buff1155Address, address potion1155Address) external'
            ]
          : [
              // AGC: V1 signatures
              'function expireBattle(bytes32 battleId, address buff1155Address) external',
              'function emergencyCancelBattle(bytes32 battleId, address buff1155Address) external'
            ]

        const p2pManager = new ethers.Contract(P2P_MANAGER_ADDRESS, P2P_MANAGER_ABI, signer)

        if (battle.type === 'emergency') {
          if (isV2Contract) {
            tx = await p2pManager.emergencyCancelBattle(
              battleId,
              OGB_TOKEN_ADDRESS,
              BUFF1155_ADDRESS,
              POTION1155_ADDRESS || '0x0000000000000000000000000000000000000000'
            )
          } else {
            tx = await p2pManager.emergencyCancelBattle(battleId, BUFF1155_ADDRESS)
          }
        } else {
          if (isV2Contract) {
            tx = await p2pManager.expireBattle(
              battleId,
              OGB_TOKEN_ADDRESS,
              BUFF1155_ADDRESS,
              POTION1155_ADDRESS || '0x0000000000000000000000000000000000000000'
            )
          } else {
            tx = await p2pManager.expireBattle(battleId, BUFF1155_ADDRESS)
          }
        }
      } else {
        // AGC: Prefer V6 PvE manager for hybrid refunds, fallback to default, then legacy V5
        const PVE_MANAGER_ADDRESS =
          import.meta.env.VITE_PVE_MANAGER_V6_ADDRESS ||
          import.meta.env.VITE_PVE_MANAGER_ADDRESS ||
          import.meta.env.VITE_PVE_MANAGER_V5_ADDRESS

        if (!PVE_MANAGER_ADDRESS) {
          throw new Error('PvE Manager address not configured')
        }

        const PVE_MANAGER_ABI = [
          'function refundBattle(bytes32 battleId) external',
          'function emergencyCancelBattle(bytes32 battleId) external'
        ]

        const pveManager = new ethers.Contract(PVE_MANAGER_ADDRESS, PVE_MANAGER_ABI, signer)

        if (battle.type === 'emergency') {
          tx = await pveManager.emergencyCancelBattle(battleId)
        } else {
          tx = await pveManager.refundBattle(battleId)
        }
      }

      console.log(`✅ Refund transaction submitted: ${tx.hash}`)
      console.log(`🔍 View on BaseScan: https://sepolia.basescan.org/tx/${tx.hash}`)

      await tx.wait()
      console.log(`✅ Refund claimed successfully!`)

      // AGC: Notify backend to clean up the battle record
      try {
        await fetch(buildBackendUrl(`api/p2p/battle/${battleId}/cleanup`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ playerAddress, refunded: true })
        })
      } catch (cleanupError) {
        console.warn('⚠️  Failed to notify backend of refund cleanup:', cleanupError)
      }

      // AGC: Invalidate cache for this address to force fresh data
      if (playerAddress) {
        const addressKey = playerAddress.toLowerCase()
        refundCache.delete(addressKey)
        console.log(`🗑️  Invalidated refund cache for ${playerAddress.slice(0, 10)}`)
      }

      // AGC: Refresh refundable battles list
      await checkRefundableBattles()

      return tx.hash
      
    } catch (err: any) {
      console.error('❌ Error claiming refund:', err)
      
      // AGC: Check if error indicates battle was already resolved/claimed
      const errorMessage = err?.message?.toLowerCase() || ''
      const isAlreadyResolved = 
        errorMessage.includes('already resolved') ||
        errorMessage.includes('already refunded') ||
        errorMessage.includes('battle does not exist') ||
        errorMessage.includes('invalid battle') ||
        errorMessage.includes('execution reverted') ||
        err?.code === 'CALL_EXCEPTION'
      
      if (isAlreadyResolved) {
        console.log(`🧹 Battle ${battleId.slice(0, 10)} appears to be already resolved - cleaning up...`)
        
        // AGC: Notify backend to clean up the stale battle record
        try {
          await fetch(buildBackendUrl(`api/p2p/battle/${battleId}/cleanup`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ playerAddress, alreadyResolved: true })
          })
        } catch (cleanupError) {
          console.warn('⚠️  Failed to notify backend of cleanup:', cleanupError)
        }
        
        // AGC: Remove from local list immediately
        setRefundableBattles(prev => prev.filter(b => b.battleId !== battleId))
        
        // AGC: Invalidate cache
        if (playerAddress) {
          const addressKey = playerAddress.toLowerCase()
          refundCache.delete(addressKey)
        }
        
        // AGC: Refresh the list
        await checkRefundableBattles()
        
        throw new Error('This battle has already been resolved or refunded. The list has been updated.')
      }
      
      throw err
    }
  }

  return {
    refundableBattles,
    hasRefunds: refundableBattles.length > 0,
    isLoading,
    error,
    refresh: checkRefundableBattles,
    claimRefund
  }
}
